/* eslint-disable array-callback-return */
import { GeoJSON, WMSCapabilities } from 'ol/format';
import { Image } from 'ol/layer';
import VectorLayer from 'ol/layer/Vector';
import { transformExtent } from 'ol/proj';
import { ImageWMS } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import DragBox from 'ol/interaction/DragBox';
import { platformModifierKeyOnly } from 'ol/events/condition';
import { createEmpty, extend, isEmpty } from 'ol/extent';

// JSTS for geometry operations like buffering
import OL3Parser from 'jsts/org/locationtech/jts/io/OL3Parser';
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp';

import Feature from 'ol/Feature';
import { LinearRing, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import { toast } from 'react-toastify';

const parser = new OL3Parser();
parser.inject(Point, LineString, LinearRing, Polygon, MultiPoint, MultiLineString, MultiPolygon);
const GEOSERVER_URL = process.env.REACT_APP_GEOSERVER_URL;

const colorConstants: any = {
    red: '#FF0000',
    blue: '#0000FF',
    green: '#008000',
    yellow: '#FFFF00',
    black: '#000000',
    // bufferColor: 'rgba(117, 117, 240, 0.2)', 
    bufferColor: 'rgba(73, 73, 231, 0.2)',
    bufferBorderColor: '#560591' // Indigo
};

const Createlayers = (sources: any, extentMap?: any) => {
    let madeLayers: any = [];
    let madeSources: any = [];
    sources?.map((layer: any) => {
        let params = {};
        if (layer?.dataType?.toLowerCase() === 'raster') {
            params = {
                FORMAT: 'image/png8',
                LAYERS: layer?.sourceName
            };
        } else {
            params = {
                LAYERS: layer?.sourceName
            };
        }

        let sourceLayer: any = new ImageWMS({
            url: GEOSERVER_URL + layer?.sourceURL,
            params: params,
            ratio: 1,
            attributions: 'geoserver',
            crossOrigin: 'anonymous'
        });

        const extent = extentMap?.[layer?.sourceName];
        let extent3857;

        if (extent && extent.length === 4) {
            extent3857 = transformExtent(
                extent,
                'EPSG:4326', // Source: Geographic
                'EPSG:3857' // Destination: Web Mercator
            );
        }

        layer.extent = extent3857;
        layer.extent4326 = extent; // EPSG:4326 extent for WMS/WCS raster download

        let createdLayer: any = new Image({
            source: sourceLayer,
            visible: layer?.visibility
        });

        createdLayer.set('type', 'source');

        createdLayer.set('sourceData', layer);

        createdLayer.getPixelValue = (coordinate: any) => {
            return new Promise((resolve, reject) => {
                sourceLayer
                    .getImage()
                    .then((image: any) => {
                        let pixel = image.getImageData(coordinate[0], coordinate[1], 1, 1).data;
                        resolve(pixel);
                    })
                    .catch(reject);
            });
        };
        madeLayers.push(createdLayer);
        madeSources.push(sourceLayer);
    });

    return [madeLayers, madeSources];
};

const hexToRgba = (hex: string, alpha: number): string => {
    const bigint = parseInt(hex.replace('#', ''), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CreateDrawlayers = (data: any) => {
    return data.map((layer: any) => {
        const obj = {
            type: 'Feature',
            geometry: layer?.coordinates,
            properties: {
                name: layer?.featureName,
                bufferDistance: layer?.bufferDistance || 0,
                featureRef: layer?.featureRef
            }
        };

        const vectorSource = new VectorSource({
            features: new GeoJSON().readFeatures(obj)
        });

        // Style assignment + buffer overlay
        vectorSource.getFeatures().forEach((feature) => {
            const geometry = feature?.getGeometry();

            // seperate buffer feature
            if (layer?.bufferDistance && Number(layer?.bufferDistance) > 0) {
                try {

                    // Buffer should start from the outer edge of the feature, not the center line
                    // So add half the line width to the buffer distance
                    const visualBufferDistance = Number(layer?.bufferDistance) + ((layer?.lineWidth || 2) / 2);

                    const jstsGeom = parser.read(geometry);
                    const buffered = BufferOp?.bufferOp(jstsGeom, visualBufferDistance);
                    const bufferedOL = parser?.write(buffered);

                    const bufferFeature = new Feature({
                        geometry: bufferedOL,
                        name: layer?.featureName,
                        bufferDistance: layer?.bufferDistance,
                        featureRef: layer?.featureRef
                    });

                    bufferFeature.setStyle(
                        new Style({
                            fill: new Fill({ color: colorConstants?.bufferColor }),
                            stroke: new Stroke({
                                color: colorConstants?.bufferBorderColor, // Purple border color
                                width: 2
                            })
                        })
                    );

                    vectorSource?.addFeature(bufferFeature);
                } catch (e) {
                    toast.error('Buffer generation failed for ' + layer?.layerName);
                }
            }

            // Main style
            let style: any;
            // 0 = solid, 100 = completely transparent
            // const opacity = layer?.opacity !== undefined ? (100 - layer?.opacity) / 100 : 0.8;

            // 100 = solid, 0 = completely transparent
            const rawOpacity = Number(layer?.opacity ?? 100);
            const clampedOpacity = Math?.min(Math?.max(rawOpacity, 0), 100);
            const opacity = clampedOpacity / 100;

            switch (layer?.coordinates?.type) {
                case 'Point':
                    style = new Style({
                        image: new CircleStyle({
                            radius: layer?.pointerSize || 6,
                            fill: new Fill({ color: layer?.pointerColor || colorConstants?.blue }),
                            stroke: new Stroke({
                                color: layer?.borderColor || colorConstants?.black,
                                width: layer?.lineWidth || 2
                            })
                        })
                    });
                    break;

                case 'LineString':
                    style = new Style({
                        stroke: new Stroke({
                            color: layer?.color || colorConstants?.red,
                            width: layer?.lineWidth || 2
                        })
                    });
                    break;

                case 'Polygon':
                default:
                    let fillColor: string | undefined;
                    let strokeColor: string | undefined;
                    let strokeWidth = layer?.lineWidth;

                    switch (layer?.layerStyle) {
                        case 'filled':
                            // Apply opacity to fill color only
                            fillColor = hexToRgba(layer?.color, opacity);
                            strokeColor = undefined;
                            break;
                        case 'outlined':
                            fillColor = undefined;
                            strokeColor = layer?.color;
                            break;
                        case 'filledPlusOutlined':
                            fillColor = hexToRgba(layer?.color, opacity);
                            strokeColor = layer?.color;
                            break;
                        default:
                            fillColor = hexToRgba(layer?.color, opacity);
                            strokeColor = layer?.color;
                    }

                    style = new Style({
                        fill: fillColor ? new Fill({ color: fillColor }) : undefined,
                        stroke: strokeColor ? new Stroke({ color: strokeColor, width: strokeWidth }) : undefined
                    });
                    break;
            }

            feature?.setStyle(style);
        });

        return new VectorLayer({
            source: vectorSource,
            visible: layer?.visibility ?? false
        });
    });
};

const highlightFeature = (feature: any, geometryType: string, layerObj: any, highlightedFeaturesRef: any) => {

    // --- highlight logic ---
    if (!feature?.get('originalStyle')) {
        feature?.set('originalStyle', feature?.getStyle());
    }

    let highlight;
    if (geometryType === 'Point' || geometryType === 'MultiPoint') {
        // ✅ Outer highlight ring based on pointerSize + lineWidth from the layer
        const pointerSize = layerObj?.pointerSize || 6;
        const lineWidth = layerObj?.lineWidth || 2;

        // 🧮 Outer ring radius should expand beyond the original circle’s stroke
        const outerRadius = pointerSize + lineWidth; // slight extra padding for visibility

        highlight = new Style({
            image: new CircleStyle({
                radius: outerRadius, // ring around the point, not inside
                stroke: new Stroke({
                    color: 'blue',
                    width: 3, // thickness of the highlight ring
                }),
                fill: new Fill({
                    color: 'rgba(0,0,0,0)', // transparent fill so it doesn't overlap the point
                }),
            }),
        });
    } else {
        // ✅ highlight for lines/polygons
        highlight = new Style({
            stroke: new Stroke({ color: 'blue', width: 4 }),
            fill: new Fill({ color: 'rgba(41, 139, 192, 0.1)' }),
        });
    }

    const baseStyle = feature?.get('originalStyle');
    const styles = Array.isArray(baseStyle) ? baseStyle : (baseStyle ? [baseStyle] : []);
    feature.setStyle([...styles, highlight]);

    if (!highlightedFeaturesRef?.current?.includes(feature)) {
        highlightedFeaturesRef?.current?.push(feature);
    }
}

const removeHeighLightOfFeature = (feature: any, highlightedFeaturesRef: any) => {
    // styles remove
    const original = feature?.get('originalStyle');
    if (original) {
        feature?.setStyle(original);
        feature?.unset('originalStyle');
    } else {
        feature?.setStyle(null);
    }

    highlightedFeaturesRef.current = highlightedFeaturesRef?.current?.filter(
        (f: any) => f !== feature
    );
}

const handleDoubleClickToSelect = (mapRef: any, dblClickHandlerRef: any, tool: string, highlightedFeaturesRef: any, features: any, handleFeatureSelection: (feature: any) => void) => {
    if (dblClickHandlerRef?.current) {
        // Remove previously attached listener to avoid duplicates
        mapRef?.current?.un("dblclick", dblClickHandlerRef?.current);
        dblClickHandlerRef.current = null;
    }

    if (tool === "Modify") {
        const dblHandler = (evt: any) => {
            // Safety: if tool changed suddenly, stop running double-click select
            if (tool !== "Modify") return;

            evt?.preventDefault();

            const clickedFeature = mapRef?.current?.forEachFeatureAtPixel(
                evt?.pixel,
                (f: any) => f
            );

            if (!clickedFeature) return;
            handleFeatureSelection(clickedFeature);
            // also heighlightFeature 
            const geometryType = clickedFeature?.getGeometry()?.getType()
            const featureRef = clickedFeature?.get('featureRef');
            const layerObj = features?.find((item: any) => item?.featureRef === featureRef);
            highlightFeature(clickedFeature, geometryType, layerObj, highlightedFeaturesRef)
        };

        dblClickHandlerRef.current = dblHandler;
        mapRef.current?.on("dblclick", dblHandler);
    }

}

const handleDragToSelect = (mapRef: any, dragBoxRef: any, tool: string, highlightedFeaturesRef: any, selectInteractionRef: any, features: any, handleFeatureSelection: (feature: any) => void) => {
    const dragBox = new DragBox({
        condition: (event) => {
            return platformModifierKeyOnly(event) && tool?.toLowerCase() === "modify";
        }
    });

    dragBoxRef.current = dragBox;
    mapRef?.current?.addInteraction(dragBox);

    // On drag start: clear highlight
    dragBox?.on("boxstart", () => {
        if (tool !== "Modify") return;

        selectInteractionRef?.current?.getFeatures()?.clear();
    });

    // On drag end: detect features inside box
    dragBox?.on("boxend", () => {
        if (tool !== "Modify") return;

        const extent = dragBox?.getGeometry()?.getExtent();
        let selectedFeature: any = null;

        // Loop all layers
        mapRef?.current?.getLayers()?.forEach((layer: any) => {
            const source = layer?.getSource?.();
            if (!source?.forEachFeatureIntersectingExtent) return;

            source.forEachFeatureIntersectingExtent(extent, (feature: any) => {
                if (!selectedFeature) selectedFeature = feature;
            });
        });

        if (selectedFeature) {
            handleFeatureSelection(selectedFeature)
            // also heighlightFeature 
            const geometryType = selectedFeature?.getGeometry()?.getType()
            const featureRef = selectedFeature?.get('featureRef');
            const layerObj = features?.find((item: any) => item?.featureRef === featureRef);
            highlightFeature(selectedFeature, geometryType, layerObj, highlightedFeaturesRef)
        };
    });
}

const fitMapToLayerDetails = (map: any, layerDetails: any) => {
    if (!map || !layerDetails) return;

    const { features } = layerDetails;
    if (!features?.length) return;

    const combinedExtent = createEmpty();
    const mapProj = map?.getView()?.getProjection()?.getCode();

    try {
        features?.forEach((featureData: any) => {
            const geomType = featureData?.coordinates?.type;
            let coords = featureData?.coordinates?.coordinates;
            if (!geomType || !coords?.length) return;

            // 🧩 Fix nesting for polygons (unwrap one level)
            if (geomType === 'Polygon' && Array.isArray(coords?.[0]?.[0]?.[0])) {
                coords = coords?.[0];
            }

            let geometry = null;
            switch (geomType) {
                case 'Polygon':
                    geometry = new Polygon(coords);
                    break;
                case 'MultiPolygon':
                    geometry = new MultiPolygon(coords);
                    break;
                case 'LineString':
                    geometry = new LineString(coords);
                    break;
                case 'MultiLineString':
                    geometry = new MultiLineString(coords);
                    break;
                case 'Point':
                    geometry = new Point(coords);
                    break;
                case 'MultiPoint':
                    geometry = new MultiPoint(coords);
                    break;
                default:
                    console.warn('⚠️ Unsupported geometry type:', geomType);
                    return;
            }

            if (geometry) {
                const geomProj = 'EPSG:3857'; // your data projection
                if (geomProj !== mapProj) {
                    geometry?.transform(geomProj, mapProj);
                }

                const geomExtent = geometry?.getExtent();

                // Slight buffer for points
                if (geomType === 'Point' || geomType === 'MultiPoint') {
                    geomExtent[0] -= 1000;
                    geomExtent[1] -= 1000;
                    geomExtent[2] += 1000;
                    geomExtent[3] += 1000;
                }

                if (!isNaN(geomExtent[0])) {
                    extend(combinedExtent, geomExtent);
                }
            }
        });

        if (!isEmpty(combinedExtent)) {
            map.updateSize();
            const size = map.getSize();

            const doFit = () => {
                map?.getView()?.fit(combinedExtent, {
                    padding: [60, 60, 60, 60],
                    duration: 800,
                    maxZoom: 14,
                });
            };

            if (!size || size[0] === 0 || size[1] === 0) {
                requestAnimationFrame(() => { map.updateSize(); doFit(); });
            } else {
                doFit();
            }
        } else {
            console.warn('⚠️ No valid geometry extent found for layerDetails');
        }
    } catch (err) {
        console.error('Error fitting map to layerDetails extent:', err);
    }
};

const fetchAndParseCapabilities = async (baseUrl: string): Promise<Record<string, number[]>> => {
    const capUrl = `${baseUrl}/wms?service=WMS&request=GetCapabilities`;
    try {
        const response = await fetch(capUrl);
        const text = await response.text();
        const parser = new WMSCapabilities();
        const capabilities = parser.read(text);
        const extentMap: Record<string, number[]> = {};
        const layers = capabilities?.Capability?.Layer?.Layer;
        layers?.forEach((capLayer: any) => {
            const sourceName = capLayer?.Name;
            if (sourceName && capLayer?.EX_GeographicBoundingBox) {
                const [minLon, minLat, maxLon, maxLat] = capLayer.EX_GeographicBoundingBox;
                extentMap[sourceName] = [minLon, minLat, maxLon, maxLat];
            }
        });
        return extentMap;
    } catch {
        return {};
    }
};

/**
 * Fit map view to the combined extent of source layers (from GetCapabilities).
 * Ensures the map zooms/pans so the layer(s) are visible when they load.
 */
const fitMapToSourceLayersExtent = (map: any, sourceLayers: any[]) => {
    if (!map || !sourceLayers?.length) return;

    const combinedExtent = createEmpty();
    try {
        sourceLayers.forEach((layer: any) => {
            const sourceData = layer?.get?.('sourceData');
            const extent = sourceData?.extent;
            if (extent && extent.length === 4 && extent.every((n: number) => !isNaN(n))) {
                extend(combinedExtent, extent);
            }
        });

        if (!isEmpty(combinedExtent)) {
            map.updateSize();
            const size = map.getSize();

            const doFit = () => {
                map.getView().fit(combinedExtent, {
                    padding: [60, 60, 60, 60],
                    duration: 800,
                    maxZoom: 14,
                });
            };

            if (!size || size[0] === 0 || size[1] === 0) {
                requestAnimationFrame(() => { map.updateSize(); doFit(); });
            } else {
                doFit();
            }
        }
    } catch (err) {
        console.error('Error fitting map to source layers extent:', err);
    }
};

/**
 * Fit map view to the mine's own stored minLatLong / maxLatLong bounds.
 * These are always correct (stored in DB) and cover all layers under the mine.
 */
const INDIA_EXTENT_4326 = [68.17, 6.55, 97.40, 35.67];

const fitMapToMineBounds = (map: any, mineData: any) => {
    if (!map) return;

    const { minLatLong, maxLatLong } = mineData ?? {};
    let extent4326 = INDIA_EXTENT_4326;
    if (minLatLong && maxLatLong) {
        const [a1, b1] = minLatLong.split(',').map(Number);
        const [a2, b2] = maxLatLong.split(',').map(Number);
        // Longitude always has larger absolute value than latitude for the same location.
        // Compare max absolute values across both points to detect which pair is lon.
        const maxAbsA = Math.max(Math.abs(a1), Math.abs(a2));
        const maxAbsB = Math.max(Math.abs(b1), Math.abs(b2));
        const [lat1, lon1] = maxAbsA > maxAbsB ? [b1, a1] : [a1, b1];
        const [lat2, lon2] = maxAbsA > maxAbsB ? [b2, a2] : [a2, b2];
        const minLat = Math.min(lat1, lat2);
        const maxLat = Math.max(lat1, lat2);
        const minLon = Math.min(lon1, lon2);
        const maxLon = Math.max(lon1, lon2);
        if (![minLat, minLon, maxLat, maxLon].some(isNaN) && maxLon > minLon && maxLat > minLat) {
            extent4326 = [minLon, minLat, maxLon, maxLat];
        }
    }
    const validCoords = extent4326 !== INDIA_EXTENT_4326;

    const extent3857 = transformExtent(extent4326, 'EPSG:4326', 'EPSG:3857');

    map.updateSize();
    const size = map.getSize();

    const doFit = () => {
        map.getView().fit(extent3857, {
            padding: [60, 60, 60, 60],
            duration: 800,
            maxZoom: validCoords ? 14 : 6,
        });
    };

    if (!size || size[0] === 0 || size[1] === 0) {
        requestAnimationFrame(() => { map.updateSize(); doFit(); });
    } else {
        doFit();
    }
};

const CreateAdminBufferLayers = (wfsFeatures: any[], bufferDistance: number, layerDisplayName: string, sourceRef: string) => {
    return wfsFeatures.map((wfsFeature: any, index: number) => {
        const featureId = `admin_buffer_${sourceRef}_${index}`;

        const obj = {
            type: 'Feature',
            geometry: wfsFeature?.geometry,
            properties: {
                name: `${layerDisplayName} - ${index + 1}`,
                bufferDistance: bufferDistance || 0,
                featureRef: featureId
            }
        };

        const vectorSource = new VectorSource({
            features: new GeoJSON().readFeatures(obj, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857'
            })
        });

        vectorSource.getFeatures().forEach((feature) => {
            const geometry = feature?.getGeometry();

            if (bufferDistance && Number(bufferDistance) > 0) {
                try {
                    const visualBufferDistance = Number(bufferDistance) + 1;
                    const jstsGeom = parser.read(geometry);
                    const buffered = BufferOp?.bufferOp(jstsGeom, visualBufferDistance);
                    const bufferedOL = parser?.write(buffered);

                    const bufferFeature = new Feature({
                        geometry: bufferedOL,
                        name: `${layerDisplayName} - ${index + 1}`,
                        bufferDistance,
                        featureRef: featureId
                    });

                    bufferFeature.setStyle(
                        new Style({
                            fill: new Fill({ color: colorConstants?.bufferColor }),
                            stroke: new Stroke({
                                color: colorConstants?.bufferBorderColor,
                                width: 2
                            })
                        })
                    );

                    vectorSource?.addFeature(bufferFeature);
                } catch (e) {
                    toast.error('Buffer generation failed for ' + layerDisplayName);
                }
            }

            // Hide the original geometry — only the buffered outline should be visible
            feature?.setStyle(new Style({}));
        });

        return new VectorLayer({
            source: vectorSource,
            visible: true
        });
    });
};

export {
    Createlayers,
    CreateDrawlayers,
    CreateAdminBufferLayers,
    fetchAndParseCapabilities,
    highlightFeature,
    removeHeighLightOfFeature,
    handleDoubleClickToSelect,
    handleDragToSelect,
    fitMapToLayerDetails,
    fitMapToSourceLayersExtent,
    fitMapToMineBounds
};
